import { useEffect } from 'react'

export interface ShortcutConfig {
  key: string
  handler: (e: KeyboardEvent) => void
  description?: string
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
}

export interface UseKeyboardShortcutsProps {
  shortcuts: ShortcutConfig[]
  enabled?: boolean
}

export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
}: UseKeyboardShortcutsProps) {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs/textareas unless explicitly requested
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(
          (e.target as HTMLElement).tagName,
        ) &&
        e.key !== 'Escape' // Allow Escape to blur/close even in inputs
      ) {
        return
      }

      for (const shortcut of shortcuts) {
        const matchKey = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const matchCtrl = !!shortcut.ctrlKey === e.ctrlKey
        const matchMeta = !!shortcut.metaKey === e.metaKey
        const matchShift = !!shortcut.shiftKey === e.shiftKey
        const matchAlt = !!shortcut.altKey === e.altKey

        if (matchKey && matchCtrl && matchMeta && matchShift && matchAlt) {
          e.preventDefault()
          shortcut.handler(e)
          return // Stop after first match
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [shortcuts, enabled])
}
